import get from "lodash/get";
import compact from "lodash/compact";
import defaults from "lodash/defaults";
import isObject from "lodash/isObject";
import last from "lodash/last";

import { browserHistory, createMemoryHistory } from "react-router";
import getAdPlaying from "selectors/ads/getAdPlaying";
import parsePlayerOptions from "utils/parsePlayerOptions";
import reduxStore from "utils/storeFactory";
import * as PlaybackStateActions from "actions/PlaybackStateActions";

import { notifyExternalOfNavigation } from "utils/externalMessaging/index";

const changeStateDefaultOptions = Object.freeze({
  destroyPlaybackStateForChapterId: null,
});

let keyHistory = [get(window, "history.state.key", "memory")];

const isOffline = !!window.offline;

// Select the route method to use.
const history = (() => {
  const parsedPlayerOptions = parsePlayerOptions(
    {},
    {
      windowLocationSearch: window.location.search,
      offline: isOffline,
    },
  );

  if (
    isOffline ||
    parsedPlayerOptions.embedded ||
    !(window && window.location)
  ) {
    const memoryHistory = createMemoryHistory();
    try {
      memoryHistory.push(window.location.pathname);
    } catch {
      // ignore errors
    }
    return memoryHistory;
  }

  return browserHistory;
})();

export class NavigationWrapper {
  routePrefix = "stories";

  stateTimer = null;

  _history;

  constructor(value) {
    // Save the value
    this._history = value;

    value.listenBefore((location) => {
      const { key } = location;

      const indexInHistory = keyHistory.indexOf(key);

      if (indexInHistory !== -1) {
        // We've already been here, meaning the browser went back. Trim the history to get
        // rid of stuff in the future.
        keyHistory = keyHistory.slice(0, indexInHistory + 1);
      } else {
        keyHistory = [...keyHistory, key];
      }
    });

    // Block navigation if we're still playing an ad
    history.listenBefore((location, callback) => {
      const adPlaying = getAdPlaying(reduxStore.getState());
      const allowNavigation = !adPlaying;
      notifyExternalOfNavigation(location.pathname);
      if (callback && allowNavigation) {
        callback();
      } else if (callback) {
        callback(false);
      }
    });
  }

  listenBefore(callback) {
    this._history.listenBefore(callback);
  }

  getHistory() {
    return this._history;
  }

  goBack() {
    if (this._history.canGo && !this._history.canGo(-1)) {
      return;
    }
    this._history.goBack();
  }

  canGoBack() {
    return keyHistory.length > 1;
  }

  generateUrl(...args) {
    const compactArguments = compact(args);
    const path = compactArguments.join("/");
    return this._history.createHref(path);
  }

  executeRouteChange(method, stateName, ...args) {
    // If there are options passed, then set defaults and remove it from `args`.
    const processedOptions = defaults(
      {},
      args && isObject(last(args)) ? args.pop() : {},
      changeStateDefaultOptions,
    );

    if (processedOptions.destroyPlaybackStateForChapterId) {
      // We need to destroy the playback state for this chapter id

      // Generate the `stateKey`. This is the key in `playbackStateStore`.
      const stateKey = `chapter-${processedOptions.destroyPlaybackStateForChapterId}`;

      reduxStore.dispatch(PlaybackStateActions.destroyPlaybackState(stateKey));
    }

    const url = `/${this.generateUrl(this.routePrefix, ...args)}${
      window.location.search
    }`;
    this._history[method](url);
  }

  replace(stateName, ...args) {
    this.executeRouteChange("replace", stateName, ...args);
  }

  push(stateName, ...args) {
    this.executeRouteChange("push", stateName, ...args);
  }
}

export default new NavigationWrapper(history);
